import React, { useMemo, useEffect, useState } from 'react';
import { Platform, TouchableOpacity, ViewProps } from 'react-native';
import styled from 'styled-components/native';
import { MaterialIcons } from '@expo/vector-icons';
import VideoPlayer from '../VideoPlayer';
import { useUserContext } from '../../context/UserContext';
import InlineTextLinks from '../InlineTextLinks';
import AlertBox from '../AlertBox';
import ImageModal from '../ImageModal';

const StyledLeftBubble = styled.View`
  max-width: 287px;
  background-color: rgba(102, 102, 102, 0.08);
  min-height: 48px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
`;

const StyledRightBubble = styled.View`
  background-color: rgba(255, 201, 102, 0.16);
  min-height: 48px;
  max-width: 287px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-left-radius: 16px;
`;

const StyledTextView = styled.View`
  margin: 12px 20px 12px 20px;
`;

const StyledImage = styled.Image`
  margin: 12px;
  width: 220px;
  height: 220px;
  border-radius: 16px;
`;

const StyleVideoPlayer = styled(VideoPlayer)`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1.8;
`;

const StyledVideoPlayerView = styled.View`
  margin: 12px;
`;

const StyledAiBadge = styled.View`
  flex-direction: row;
  align-items: center;
  margin: 0px 20px 8px 20px;
  opacity: 0.7;
`;

const StyledAiText = styled.Text`
  font-size: 10px;
  color: #555;
  margin-left: 4px;
  font-weight: bold;
`;

export type ListItemChatBubbleProps = ViewProps & {
  left?: boolean;
  imageUrl?: string;
  videoUrl?: string;
  text: string;
  embedded?: boolean;
  isAiGenerated?: boolean;
};

const ListItemChatBubble: React.FC<ListItemChatBubbleProps> = ({
  left,
  imageUrl,
  videoUrl,
  text,
  embedded,
  isAiGenerated
}) => {
  const { accessToken } = useUserContext();
  const [uri, setUri] = useState('');
  const [error, setError] = useState<string>();
  const url = imageUrl || videoUrl;
  const StyledBubble = left ? StyledLeftBubble : StyledRightBubble;
  const mouseOverProps = useMemo(() => {
    if (Platform.OS !== 'web') return {};
    return {
      onMouseOver: () => {
        // console.log('onMouseOver');
      },
      onMouseOut: () => {
        // console.log('onMouseOut');
      }
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (imageUrl) {
          const querySpacing = imageUrl.includes('?') ? '&' : '?';
          const response = await fetch(
            `${imageUrl}${querySpacing}token=${accessToken}`
          );
          if (response.status === 200) {
            const blob = await response.blob();
            const dataUri = await blobToBase64(blob);
            setUri(dataUri);
          }
        }
      } catch (error) {
        setError((error as any)?.message ?? 'Error loading file.');
      }
    })();
  }, [accessToken, imageUrl, url]);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  return (
    <StyledBubble {...mouseOverProps}>
      {!!imageUrl && (
        <>
          <TouchableOpacity
            onPress={() => {
              if (embedded && Platform.OS === 'web') {
                // if embedded in admin portal notify of focus
                window.parent?.postMessage(
                  {
                    source: 'iframe',
                    type: 'open-image',
                    imageUrl: uri || imageUrl
                  },
                  '*'
                );
              } else {
                setOpenImageDialog(true);
              }
            }}
          >
            <StyledImage
              source={{
                uri: uri || imageUrl
              }}
              resizeMode='cover'
            />
          </TouchableOpacity>
          {openImageDialog && (
            <ImageModal
              visible
              imageUrl={`${imageUrl}&token=${accessToken}`}
              onRequestClose={() => setOpenImageDialog(false)}
            />
          )}
        </>
      )}
      {!!videoUrl && (
        <StyledVideoPlayerView>
          <StyleVideoPlayer
            disableAutoPlay
            videoSource={{
              uri: `${videoUrl}${
                url ? (videoUrl.includes('?') ? '&' : '?') : ''
              }token=${accessToken}`
            }}
          />
        </StyledVideoPlayerView>
      )}
      {!videoUrl && !imageUrl && (
        <StyledTextView>
          <InlineTextLinks text={text} />
        </StyledTextView>
      )}
      {isAiGenerated && (
        <StyledAiBadge>
          <MaterialIcons name='smart-toy' size={12} color='#555' />
          <StyledAiText>AI Assistant</StyledAiText>
        </StyledAiBadge>
      )}
      {error && <AlertBox severity='error'>{error}</AlertBox>}
    </StyledBubble>
  );
};

export default ListItemChatBubble;

function blobToBase64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      }
      reject('Not a data uri');
    };
    reader.readAsDataURL(blob);
  });
}
